import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { postService } from '@services/api/posts'
import SuccessModal from '@components/ui/SuccessModal'
import { getCategoryFields } from '@constants/categoryFields'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import { categoryService } from '@services/api/categories'
import apiClient from '@services/api/client'
import { isInFreeTrial, getFreeTrialDaysLeft } from '@utils/freeTrial'

function CreatePostPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [apiCategories, setApiCategories] = useState([])
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    price: '',
    discount: '',
    delivery: 'gratuite',
    condition: '',
    images: [],
    imageFiles: [],
    imageLimit: 3,
    mainImageIndex: 0,
    categoryFields: {}
  })

  const [categoryFieldsConfig, setCategoryFieldsConfig] = useState(null)

  useEffect(() => {
    categoryService.getCategories()
      .then(res => {
        const list = res.data || res || []
        setApiCategories(Array.isArray(list) ? list : [])
      })
      .catch(() => setApiCategories([]))
  }, [])

  // Mettre à jour les champs spécifiques quand la catégorie change
  useEffect(() => {
    if (formData.category) {
      const selectedCat = apiCategories.find(c => String(c.categorie_id) === String(formData.category))
      const catName = selectedCat?.libele_categorie || ''
      const fields = getCategoryFields(catName || formData.category)
      setCategoryFieldsConfig(fields)
      setFormData(prev => ({ ...prev, categoryFields: {}, categoryName: catName }))
    } else {
      setCategoryFieldsConfig(null)
    }
  }, [formData.category, apiCategories])

  const categories = apiCategories

  const discounts = [
    'Choisissez un champ',
    '5%',
    '10%',
    '15%',
    '20%',
    '25%',
    '30%',
    '40%',
    '50%',
    '60%',
    '70%',
    '80%'
  ]

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    if (formData.images.length + files.length <= formData.imageLimit) {
      const newImages = files.map(file => URL.createObjectURL(file))
      const newFiles = [...formData.imageFiles, ...files]
      setFormData({ 
        ...formData, 
        images: [...formData.images, ...newImages],
        imageFiles: newFiles
      })
    } else {
      alert(`Vous pouvez ajouter maximum ${formData.imageLimit} photos`)
    }
  }

  const handleImageLimitChange = (limit) => {
    // Si on réduit la limite et qu'on a déjà trop d'images, on garde uniquement les premières
    const newImages = formData.images.slice(0, limit)
    const newFiles = formData.imageFiles.slice(0, limit)
    setFormData({ ...formData, imageLimit: limit, images: newImages, imageFiles: newFiles })
  }

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index)
    const newFiles = formData.imageFiles.filter((_, i) => i !== index)
    // Si on supprime l'image principale, la première devient principale
    const newMainIndex = formData.mainImageIndex === index ? 0 : 
                        formData.mainImageIndex > index ? formData.mainImageIndex - 1 : formData.mainImageIndex
    setFormData({ ...formData, images: newImages, imageFiles: newFiles, mainImageIndex: newMainIndex })
  }

  const setMainImage = (index) => {
    setFormData({ ...formData, mainImageIndex: index })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (formData.images.length === 0) {
      alert('Veuillez ajouter au moins une photo')
      return
    }

    if (!formData.title || !formData.category || !formData.location || !formData.description || !formData.price) {
      alert('Veuillez remplir tous les champs obligatoires')
      return
    }

    setIsSubmitting(true)

    try {
      const clientId = user?.client_id || user?.id

      if (!clientId) {
        toast.error('Vous devez être connecté pour créer une annonce')
        setIsSubmitting(false)
        return
      }

      const formDataToSend = new FormData()
      formDataToSend.append('libele', formData.title)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('post_client_id', clientId)
      formDataToSend.append('post_categorie_id', formData.category)
      formDataToSend.append('type', '2') // 2 = image/article
      formDataToSend.append('prix', formData.price)
      formDataToSend.append('siteWeb', formData.location)

      // État
      if (formData.condition) {
        formDataToSend.append('etat_appareil', formData.condition)
      }

      // Réduction : prix = prix original, new_price = prix après remise, marge_reduite = %
      if (formData.discount && formData.discount !== 'Choisissez un champ') {
        const pct = parseInt(formData.discount)
        if (pct > 0 && pct < 100) {
          const newPrice = Math.round(parseFloat(formData.price) * (1 - pct / 100))
          formDataToSend.append('new_price', newPrice)
          formDataToSend.append('marge_reduite', pct)
        }
      }

      // Livraison
      if (formData.delivery) {
        formDataToSend.append('livraison', formData.delivery)
      }

      // Champs spécifiques à la catégorie — envoyés comme colonnes directes
      if (formData.categoryFields) {
        Object.entries(formData.categoryFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            formDataToSend.append(key, Array.isArray(value) ? JSON.stringify(value) : value)
          }
        })
      }

      // Images → champ "files"
      formData.imageFiles.forEach((file) => {
        formDataToSend.append('files[]', file)
      })

      const response = await postService.createPost(formDataToSend)

      const createdPost = response.data || response

      if (isInFreeTrial(user)) {
        // Période d'essai : le backend auto-approuve pendant createPost (status = 2)
        const daysLeft = getFreeTrialDaysLeft(user)
        toast.success(`✅ Annonce publiée ! Période d'essai — ${daysLeft} jour${daysLeft > 1 ? 's' : ''} restant${daysLeft > 1 ? 's' : ''}.`)
        setTimeout(() => navigate('/dashboard'), 1500)
      } else {
        // Hors période d'essai : rediriger vers le paiement
        navigate('/payment-article', {
          state: {
            postData: { ...formData, categoryName: formData.categoryName || '' },
            postId: createdPost.post_id,
            imageCount: formData.imageLimit,
            isEdit: false
          }
        })
      }

    } catch (error) {
      console.error('Erreur lors de la création de l\'annonce:', error)
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Une erreur est survenue lors de la publication de votre annonce. Veuillez réessayer.'
      alert(`Erreur: ${errorMessage}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    // Rediriger vers la page d'accueil ou le dashboard
    navigate('/ligdi')
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }
  
  const handleCategoryFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      categoryFields: {
        ...formData.categoryFields,
        [fieldName]: value
      }
    })
  }

  return (
    <div className="min-h-screen bg-[#fefcf8]">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-2 sm:px-4 py-3 sm:py-8">
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 md:p-8 lg:p-12 overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Veuillez écrire un titre clair pour votre article"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                    required
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.categorie_id} value={cat.categorie_id}>{cat.libele_categorie}</option>
                    ))}
                  </select>
                </div>

                {/* Situation géographique */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Situation géographique<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Votre emplacement"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez votre article"
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                    required
                  ></textarea>
                </div>

                {/* État */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    État
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="">Sélectionnez l'état</option>
                    <option value="neuf">Neuf</option>
                    <option value="scelle">Scellé</option>
                    <option value="reconditionne">Reconditionné</option>
                    <option value="quasi_neuf">Quasi neuf</option>
                    <option value="excellent">Excellent</option>
                    <option value="bon">Bon état</option>
                    <option value="moyen">État moyen</option>
                    <option value="usage">Usagé</option>
                  </select>
                </div>
                
                {/* Champs spécifiques à la catégorie */}
                {categoryFieldsConfig && (
                  <div className="col-span-2 mt-6">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-[#001F3F] mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Informations spécifiques - {formData.category}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {categoryFieldsConfig.fields.map((field) => (
                          <div key={field.name} className={field.type === 'checkbox' ? 'md:col-span-2' : ''}>
                            <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                              {field.label}
                              {field.required && <span className="text-red-600">*</span>}
                            </label>
                            
                            {field.type === 'select' && (
                              <select
                                value={formData.categoryFields[field.name] || ''}
                                onChange={(e) => handleCategoryFieldChange(field.name, e.target.value)}
                                required={field.required}
                                className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                                style={{
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 0.75rem center',
                                  backgroundSize: '1.5em 1.5em'
                                }}
                              >
                                <option value="">Sélectionnez...</option>
                                {field.options.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            )}
                            
                            {(field.type === 'text' || field.type === 'number') && (
                              <div className="relative">
                                <input
                                  type={field.type}
                                  value={formData.categoryFields[field.name] || ''}
                                  onChange={(e) => handleCategoryFieldChange(field.name, e.target.value)}
                                  placeholder={field.placeholder}
                                  required={field.required}
                                  min={field.min}
                                  max={field.max}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                                />
                                {field.suffix && (
                                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                    {field.suffix}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {field.type === 'checkbox' && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {field.options.map((option) => (
                                  <label key={option} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(formData.categoryFields[field.name] || []).includes(option)}
                                      onChange={(e) => {
                                        const current = formData.categoryFields[field.name] || []
                                        const updated = e.target.checked
                                          ? [...current, option]
                                          : current.filter(item => item !== option)
                                        handleCategoryFieldChange(field.name, updated)
                                      }}
                                      className="w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37]"
                                    />
                                    <span className="text-sm text-gray-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Colonne droite */}
              <div className="space-y-6">
                {/* Ajouter une photo */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Ajouter une photo
                  </label>
                  
                  {/* Onglets de sélection du nombre d'images */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-600 mb-2">Choisissez le nombre d'images :</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(3)}
                        className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                          formData.imageLimit === 3
                            ? 'bg-[#D4AF37] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>3 photos</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(6)}
                        className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                          formData.imageLimit === 6
                            ? 'bg-[#D4AF37] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <span>6 photos</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(9)}
                        className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                          formData.imageLimit === 9
                            ? 'bg-[#D4AF37] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                          </svg>
                          <span>9 photos</span>
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-3">
                    Cliquez sur une image pour la définir comme image principale (affichée en premier).
                  </p>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {/* Images uploadées */}
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative aspect-square border-2 border-gray-300 rounded-lg overflow-hidden group">
                        <img src={img} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                        
                        {/* Badge Image Principale */}
                        {formData.mainImageIndex === index && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF37] text-white text-xs rounded-md font-semibold shadow-lg flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Principale
                          </div>
                        )}
                        
                        {/* Bouton Supprimer */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 shadow-lg"
                          title="Supprimer l'image"
                        >
                          ×
                        </button>
                        
                        {/* Bouton Définir comme principale (visible au survol si ce n'est pas déjà principale) */}
                        {formData.mainImageIndex !== index && (
                          <button
                            type="button"
                            onClick={() => setMainImage(index)}
                            className="absolute bottom-2 left-2 right-2 px-2 py-1.5 bg-white/90 hover:bg-white text-[#001F3F] text-xs rounded-md font-medium opacity-0 group-hover:opacity-100 transition-opacity shadow-md flex items-center justify-center gap-1"
                            title="Définir comme image principale"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            Principale
                          </button>
                        )}
                      </div>
                    ))}
                    
                    {/* Boutons d'upload restants */}
                    {[...Array(formData.imageLimit - formData.images.length)].map((_, index) => (
                      <label
                        key={index}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] hover:bg-gray-50 transition-colors"
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          multiple
                        />
                        <svg className="w-10 h-10 text-gray-400 mb-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                        </svg>
                      </label>
                    ))}
                  </div>
                  
                  {/* Indicateur du nombre d'images */}
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-600">
                      {formData.images.length} / {formData.imageLimit} photos ajoutées
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Prix et Réduction */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Prix */}
              <div>
                <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                  Prix<span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Veuillez écrire un titre clair pour votre article"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  required
                />
              </div>

              {/* Appliquer une réduction */}
              <div>
                <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                 Appliquer une réduction
                </label>
                <select
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  {discounts.map((discount, index) => (
                    <option key={index} value={discount}>{discount}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Livraison */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-[#001F3F] mb-3">
                Livraison<span className="text-red-600">*</span>
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="delivery"
                    value="gratuite"
                    checked={formData.delivery === 'gratuite'}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#D4AF37] border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="ml-2 text-sm text-[#001F3F]">Gratuite</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="delivery"
                    value="payante"
                    checked={formData.delivery === 'payante'}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#D4AF37] border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="ml-2 text-sm text-[#001F3F]">Payante</span>
                </label>
              </div>
            </div>

            {/* Bouton Publier */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-20 py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publication en cours...
                  </span>
                ) : (
                  'Publier'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de succès */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="localhost:3000 indique"
        message="Votre annonce a été publiée avec succès !"
      />
    </div>
  )
}

export default CreatePostPage
